import { type ReactNode, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { type PimVariantAttributeV1 } from '@redteclab/api/clients/bully'
import { IconCaretDown, IconCaretUp } from '@redteclab/icons'

import { ATTRIBUTE_NAME_PACKAGE_SIZE } from '../../product-page-context/ATTRIBUTE_NAME'
import { type SelectionEntry } from '../../product-page-context/useProductPageAttributeSelectionControls'

const COLLAPSIBLE_ITEMS_THRESHOLD = 6

const labelShowMore = (count: number): ReactNode => (
  <>
    <FormattedMessage
      id="general.label.showNMore"
      values={{
        count,
      }}
    />
    <IconCaretDown className="size-5" />
  </>
)

const labelShowLess = (
  <>
    <FormattedMessage id="general.label.showLess" />
    <IconCaretUp className="size-5" />
  </>
)

interface UseAttributeSelectionCollapsibleProps {
  isSingleGroup: boolean
  metaAttributeData: PimVariantAttributeV1
  selectionEntries: SelectionEntry[]
}

interface UseAttributeSelectionCollapsibleReturnType {
  handleLabelClick?: () => void
  labelContent: ReactNode
  // entries with index less or eq, this shall be displayed; higher shown
  maxVisibleIndex: number
  showLabel: boolean
}

type CollapsibleStateType = 'hideAll' | 'none' | 'showAll' | 'threshold'

const getInitialCollapsibleState = (
  metaAttribute: PimVariantAttributeV1,
  isSingleGroup: boolean,
  selectionEntries: SelectionEntry[],
): CollapsibleStateType => {
  if (metaAttribute.variant_attribute === ATTRIBUTE_NAME_PACKAGE_SIZE) {
    return 'none'
  }

  if (isSingleGroup) {
    return 'none'
  }

  if (selectionEntries.length > COLLAPSIBLE_ITEMS_THRESHOLD) {
    return 'threshold'
  }

  return 'none'
}

export const useAttributeSelectionCollapsible = ({
  isSingleGroup,
  metaAttributeData,
  selectionEntries,
}: UseAttributeSelectionCollapsibleProps): UseAttributeSelectionCollapsibleReturnType => {
  const initialCollapsibleState = getInitialCollapsibleState(
    metaAttributeData,
    isSingleGroup,
    selectionEntries,
  )

  const [collapsibleState, setCollapsibleState] =
    useState<CollapsibleStateType>(initialCollapsibleState)

  const handleLabelClick = (state: CollapsibleStateType): void => {
    switch (state) {
      case 'hideAll': {
        setCollapsibleState('showAll')
        break
      }

      case 'showAll': {
        setCollapsibleState('hideAll')
        break
      }

      case 'threshold': {
        setCollapsibleState('showAll')
        break
      }

      default:
    }
  }

  const labelContent = (state: CollapsibleStateType): ReactNode | undefined => {
    switch (state) {
      case 'hideAll': {
        return labelShowMore(selectionEntries.length)
      }

      case 'showAll': {
        return labelShowLess
      }

      case 'threshold': {
        return labelShowMore(
          selectionEntries.length - COLLAPSIBLE_ITEMS_THRESHOLD,
        )
      }

      default: {
        return undefined
      }
    }
  }

  const getMaxDisplayedIndex = (state: CollapsibleStateType): number => {
    switch (state) {
      case 'hideAll': {
        return 0
      }

      case 'threshold': {
        return COLLAPSIBLE_ITEMS_THRESHOLD
      }

      default: {
        return selectionEntries.length
      }
    }
  }

  return {
    handleLabelClick: (): void => {
      handleLabelClick(collapsibleState)
    },
    labelContent: labelContent(collapsibleState),
    maxVisibleIndex: getMaxDisplayedIndex(collapsibleState),
    showLabel: collapsibleState !== 'none',
  }
}
